import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, Phone, MessagesSquare, HeartHandshake, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import BookingLink from "../components/BookingLink";
import CtaSection from "../components/CtaSection";
import { Reveal, RevealGroup, RevealItem } from "../components/Reveal";
import { BUSINESS, PATHWAYS } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

function HeroLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.05, delay, ease: EASE }}>
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 sm:pt-32 lg:pt-36" data-testid="hero-section">
      <div aria-hidden className="absolute -right-48 -top-32 h-[620px] w-[620px] rounded-full bg-sage/70 blur-3xl" />
      <div aria-hidden className="absolute -left-48 bottom-0 h-[440px] w-[440px] rounded-full bg-sage/50 blur-3xl" />
      <div className="container-shell relative pb-16 pt-4 lg:pb-20">
        <div className="max-w-2xl">
          <h1 className="font-serif text-5xl leading-[1.04] tracking-tight text-olive sm:text-6xl lg:text-7xl" style={{ fontFamily: "'Source Sans 3', system-ui, sans-serif", fontWeight: 700 }}>
            <HeroLine delay={0.2}>Clear numbers for your business.</HeroLine>
            <HeroLine delay={0.34}><em className="not-italic text-pine" style={{ fontVariantLigatures: "none", letterSpacing: "0.005em" }}>Confident next steps.</em></HeroLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65, ease: EASE }} className="mt-8 max-w-xl text-base leading-relaxed text-moss sm:text-lg">
            We offer accounting solutions for your company.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease: EASE }} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <a href={BUSINESS.phoneHref} data-testid="hero-chat-button" className="btn-primary">
              Let’s Chat <ArrowUpRight className="h-4 w-4" />
            </a>
            <BookingLink page="hero-quote" action="get_quote" dataTestId="hero-quote-button" className="btn-outline">
              Get a Quote <ArrowRight className="h-4 w-4" />
            </BookingLink>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.05, ease: EASE }} className="mt-8 text-sm font-semibold text-olive" data-testid="hero-reassurance">
            Talk directly with the person who will handle your books.
          </motion.p>
        </div>
      </div>
    </section>
  );
}

function FullCycle() {
  const steps = [
    { t: "Set up", b: "A clean foundation. Accounts and structure done right from day one." },
    { t: "Record", b: "Every transaction captured and categorized, month after month." },
    { t: "Reconcile", b: "Books matched to the bank, with every difference explained." },
    { t: "Pay and collect", b: "Bills out on time, invoices in on time. No memory required." },
    { t: "Report", b: "Financial statements you can actually read and act on." },
    { t: "Plan", b: "Ready for tax season and ready for decisions." },
  ];
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % steps.length), 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden bg-pine" data-testid="full-cycle-section">
      <div className="bg-glow-dark" aria-hidden />
      <div className="container-shell relative py-16 lg:py-20">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="font-serif text-lg italic !text-sage/70">Full cycle accounting for your business.</p>
          <h2 className="mx-auto mt-4 font-serif text-3xl leading-tight tracking-tight text-sage sm:text-4xl">One team for the entire accounting cycle.</h2>
          <p className="mx-auto mt-5 text-base leading-relaxed text-sage/80 sm:text-lg">Most providers handle one piece of your accounting. We keep the whole cycle turning so nothing falls between vendors and nothing waits on a handoff.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-12 flex max-w-4xl flex-wrap justify-center gap-3" role="tablist" aria-label="The accounting cycle">
            {steps.map((s, i) => (
              <button key={s.t} role="tab" aria-selected={active === i} data-testid={`full-cycle-step-${i + 1}`} onMouseEnter={() => setActive(i)} onClick={() => setActive(i)} className={`relative overflow-hidden rounded-full px-5 py-3 text-sm font-extrabold transition-colors duration-300 ${active === i ? "bg-sage text-pine" : "bg-white/5 text-sage/60 hover:text-sage"}`}>
                {s.t}
                {active === i && (
                  <motion.span key={`bar-${active}`} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 3.2, ease: "linear" }} className="absolute inset-x-0 bottom-0 h-0.5 bg-pine/60" />
                )}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mx-auto mt-8 min-h-[72px] max-w-2xl text-center">
          <AnimatePresence mode="wait">
            <motion.p key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35, ease: EASE }} className="text-base leading-relaxed text-sage/85 sm:text-lg" data-testid="full-cycle-step-description">
              {steps[active].b}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function Pathways() {
  return (
    <section className="py-14 lg:py-20" data-testid="pathways-section">
      <div className="container-shell">
        <Reveal className="max-w-3xl">
          <p className="eyebrow">Three Targets We Focus On</p>
          <h2 className="mt-5 text-balance font-serif text-4xl leading-tight tracking-tight text-olive sm:text-5xl">Where do your numbers need attention?</h2>
        </Reveal>
        <RevealGroup className="mt-14 grid gap-x-10 gap-y-14 md:grid-cols-3">
          {PATHWAYS.map((p, i) => (
            <RevealItem key={p.to} className="h-full">
              <Link to={p.to} data-testid={`pathway-link-${p.title.toLowerCase().replace(/\s+/g, "-")}`} className="group flex h-full flex-col transition-colors duration-300">
                <span className="font-serif text-4xl text-olive/30">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-serif text-2xl text-olive">{p.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-moss">{p.body}</p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-pine">
                  Learn more <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
}

function PainPoints() {
  const items = [
    { t: "Unpaid invoices piling up", b: "We track every invoice, follow up on what is owed, and keep cash moving." },
    { t: "Books falling behind", b: "We catch you up, then keep you current month after month." },
    { t: "Payroll headaches", b: "Clean payroll records, coordinated with your provider. No surprises on payday." },
    { t: "Tax season panic", b: "Your records stay ready all year, so deadlines stop being emergencies." },
    { t: "Guessing at cash flow", b: "Clear reports that show what came in, what went out, and what comes next." },
    { t: "Receipts everywhere", b: "One organized system for every receipt, statement, and record." },
  ];
  const [picked, setPicked] = useState(null);

  return (
    <section className="relative overflow-hidden py-6 lg:py-10" data-testid="pain-points-section">
      <div className="container-shell relative">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="text-balance font-serif text-3xl leading-tight tracking-tight text-olive sm:text-4xl">We target these concerns.</h2>
          <p className="mx-auto mt-5 text-base leading-relaxed text-moss sm:text-lg">Tap what is consuming your time. There is a good chance it is exactly the work we take over and resolve.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-3">
            {items.map((item, i) => (
              <button key={item.t} onClick={() => setPicked(i)} aria-pressed={picked === i} data-testid={`pain-chip-${i + 1}`} className={`rounded-full border px-5 py-2.5 text-sm font-bold transition-colors duration-300 ${picked === i ? "border-pine bg-pine text-sage" : "border-olive/30 bg-white text-olive hover:border-pine"}`}>
                {item.t}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mx-auto mt-2 max-w-3xl text-center">
          <AnimatePresence mode="wait">
            {picked !== null && (
              <motion.div key={picked} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35, ease: EASE }} data-testid="pain-response">
                <p className="font-serif text-2xl italic leading-snug text-pine sm:text-3xl">{items[picked].b}</p>
                <BookingLink page="pain-points" dataTestId="pain-book-button" className="btn-primary mt-7">
                  Hand it to us <ArrowUpRight className="h-4 w-4" />
                </BookingLink>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function SunsetBand() {
  return (
    <section className="relative overflow-hidden" data-testid="sunset-band-section">
      <img src="/img/photo-sunset-band.webp" alt="Mountain ridges under a golden sunset sky" width="628" height="286" loading="lazy" className="h-[300px] w-full object-cover sm:h-[340px]" style={{ objectPosition: "center 60%" }} />
      <div aria-hidden className="absolute inset-0 bg-pine/65" />
      <div className="absolute inset-0 flex items-center">
        <div className="container-shell">
          <Reveal>
            <p className="eyebrow !text-sage/70">Why clients stay</p>
            <h2 className="mt-4 max-w-2xl text-balance font-serif text-3xl leading-tight text-sage sm:text-4xl lg:text-5xl">{BUSINESS.tagline}</h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-sage/85 sm:text-lg">The person keeping your books should be the person who knows your name. That is the firm we built.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Partner() {
  const points = [
    { icon: Phone, t: "Direct access", b: "Call and reach a real person who already knows your file." },
    { icon: MessagesSquare, t: "Plain language", b: "Every report explained the way we’d explain it to family." },
    { icon: HeartHandshake, t: "Human-sized client list", b: "Nothing and no one gets lost in the shuffle." },
  ];
  return (
    <section className="relative overflow-hidden py-20 lg:py-24" data-testid="partner-section">
      <img src="/img/photo-financial-partner.webp" alt="" aria-hidden width="384" height="536" loading="lazy" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "center 35%" }} />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/85 lg:to-white/40" />
      <div className="container-shell relative">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Meet your accounting team</p>
          <h2 className="mt-5 text-balance font-serif text-4xl leading-tight tracking-tight text-olive sm:text-5xl">Work with a person, not a rotation.</h2>
          <p className="mt-6 text-base leading-relaxed text-moss sm:text-lg">Big firms pass you between departments. Here, the person who answers your call is the person who does the work. Your books stay with a small team that knows your name, your industry, and what you’re building.</p>
          <ul className="mt-8 space-y-5">
            {points.map((p) => (
              <li key={p.t} className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sage text-olive"><p.icon className="h-5 w-5" /></span>
                <div>
                  <p className="font-bold text-ink">{p.t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-moss">{p.b}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <BookingLink page="partner" dataTestId="partner-book-button" className="btn-primary">
              Book a Consultation <ArrowUpRight className="h-4 w-4" />
            </BookingLink>
            <Link to="/about" data-testid="partner-about-link" className="btn-outline">
              Meet the team <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function SupportNationwide() {
  const promises = ["Personalized support", "Long-term relationships", "Your goals. Our commitment."];
  const trackQuote = () => {
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get_quote", page: "nationwide" }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* tracking must never block the click */
    }
  };
  return (
    <section className="relative" data-testid="support-nationwide-section">
      <div className="container-shell">
        <img src="/img/photo-team-laughing.webp" alt="Three colleagues laughing together around a laptop" width="620" height="278" loading="lazy" className="h-auto w-full" />
      </div>
      <div className="container-shell relative z-10 grid items-start gap-14 py-20 lg:grid-cols-2 lg:py-28">
        <Reveal>
          <p className="eyebrow">Real people. Real support.</p>
          <h2 className="mt-5 text-balance font-serif text-4xl leading-tight tracking-tight text-olive sm:text-5xl">A team that answers. Wherever you are.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-moss sm:text-lg">Much of our work is handled remotely, so we can keep your books current whether you’re across town or across the country. No matter where you are, we’re here to help.</p>
          <ul className="mt-8 space-y-3">
            {promises.map((p) => (
              <li key={p} className="flex items-center gap-3 text-base font-semibold text-olive"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-pine" />{p}</li>
            ))}
          </ul>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to="/contact" onClick={trackQuote} data-testid="nationwide-quote-button" className="btn-primary">
              Get a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={BUSINESS.phoneHref} data-testid="nationwide-phone-link" className="btn-outline">
              <Phone className="h-4 w-4" /> {BUSINESS.phone}
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="flex items-center gap-6 self-start rounded-md border border-line bg-white p-6">
          <img src="/img/photo-us-map.webp" alt="A green map of the United States marked with client locations" width="410" height="225" loading="lazy" className="hidden w-44 shrink-0 sm:block" />
          <div>
            <h3 className="flex items-center gap-2 font-serif text-xl text-olive"><MapPin className="h-5 w-5 text-pine" /> Serving businesses across the entire U.S.</h3>
            <p className="mt-2 text-sm leading-relaxed text-moss">Full cycle accounting from coast to coast. Local feel, national reach.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Business Bookkeeping & Accounting | Directive Optive Solutions"
        description="Full cycle bookkeeping and accounting for growing businesses: reconciled ledgers, clear financial reports, and over a decade of accounting management experience."
        path="/"
      />
      <Hero />
      <FullCycle />
      <SunsetBand />
      <Pathways />
      <Partner />
      <SupportNationwide />
      <PainPoints />
      <CtaSection heading="Book a consultation or get a quote." />
    </>
  );
}
